import { fetchClient } from "@/lib/api/client";
import { fetchClientLogo } from "@/lib/api/logo";

export interface ClientBranding {
  /** Data URL of the account's own logo; null means fall back to the bundled mark. */
  logoDataUrl: string | null;
  /** Client title, only when the logo also loaded — same pairing rule as the PDF masthead. */
  name: string | null;
  /** Client city, for the sub-label under the name. */
  city: string | null;
}

const UNBRANDED: ClientBranding = { logoDataUrl: null, name: null, city: null };

/**
 * The account's own brand mark and name for the app chrome. Both halves are required: a logo
 * without a profile (or the reverse) falls back to the bundled Global GPS pair rather than mixing
 * the two brands — the same rule `resolveMasthead()` applies to the PDF header.
 *
 * Unlike the PDF path this does not check that the image is one jsPDF can embed; a browser `<img>`
 * renders SVG and GIF just as well, and `fetchClientLogo()` has already required an `image/*` mime.
 *
 * Neither source rejects — both swallow their errors and return `null` — so an account with no
 * branding is the ordinary case, not a failure.
 */
export async function fetchClientBranding(): Promise<ClientBranding> {
  const [logoDataUrl, client] = await Promise.all([fetchClientLogo(), fetchClient()]);
  if (!logoDataUrl || !client?.title) return UNBRANDED;
  return { logoDataUrl, name: client.title, city: client.city || null };
}
